import React, { useEffect, useState } from "react";
import LineChartStatus from "../../../Components/Charts/LineChart";
import { Dropdown } from "react-bootstrap";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { useLanguage } from "../../../Translations/LenguageContext";
import CustomDatePicker from "../../../Components/DatePicker";
import LoaderSimChartData from "../../../Loaders/LoaderSimChartData";
import LoaderSimChart from "../../../Loaders/LoaderSimChart";

const GeoOverviewChart = ({
    data,
    prompts,
    engines,
    fetchChartData,
    loadingChartData,
    selectedPromptId,
    setSelectedPromptId,
    selectedEnginesChart,
    setSelectedEnginesChart,
    selectedMetricKey,
    setSelectedMetricKey,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
}) => {
    const { t } = useLanguage()
    const [loadingDefault, setLoadingDefault] = useState(true)
    const [filterPrompts, setFilterPrompts] = useState("")
    const [selectedPrompt, setSelectedPrompt] = useState()
    const [daysLenght, setDaysLength] = useState(0)
    const [lineStyle, setLineStyle] = useState("")
    const lineStyles = ["natural", "linear"]
    const selectedEnginesKey = JSON.stringify(selectedEnginesChart);

    const metrics = [
        {
            key: "effectiveness_score",
            label: t.geo.effectiveness
        },
        {
            key: "visibility",
            label: t.geo.visibility
        },
        {
            key: "sentiment",
            label: t.geo.sentiment
        },
        {
            key: "position",
            label: t.geo.position
        }
    ]

    const [selectedMetric, setSelectedMetric] = useState(metrics[0].label)

    const filteredPrompts = (prompts || []).filter((prompt) => {
        const search = filterPrompts.trim().toLowerCase();

        return (
            String(prompt.id).toLowerCase().includes(search) ||
            (prompt.prompt || "").toLowerCase().includes(search)
        );
    });

    useEffect(() => {
        if (!fromDate || !toDate) return;
        const from = new Date(`${fromDate}T00:00:00Z`);
        const to = new Date(`${toDate}T00:00:00Z`);
        const diffTime = to.getTime() - from.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        setDaysLength(diffDays + 1)
        setLineStyle("natural")
    }, [fromDate, toDate]);

    useEffect(() => {
        if (!prompts?.length || !engines?.length) return;

        const today = new Date();
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 6);

        const formatDate = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        if (!selectedMetricKey) {
            setSelectedMetricKey(metrics[0].key);
            setSelectedMetric(metrics[0].label);
        }

        if (!selectedPromptId) {
            setSelectedPromptId(prompts[0].id);
            setSelectedPrompt(prompts[0].prompt);
        }

        if (!selectedEnginesChart?.length) {
            setSelectedEnginesChart([engines[0]]);
        }

        if (!fromDate) setFromDate(formatDate(weekAgo));
        if (!toDate) setToDate(formatDate(today));

        setLoadingDefault(false)
    }, [
        prompts,
        engines,
        selectedMetricKey,
        selectedPromptId,
        selectedEnginesChart,
        fromDate,
        toDate,
        setSelectedMetricKey,
        setSelectedPromptId,
        setSelectedEnginesChart,
        setFromDate,
        setToDate,
    ]);

    useEffect(() => {
        if (
            !selectedPromptId ||
            !selectedEnginesChart?.length ||
            !selectedMetricKey ||
            !fromDate ||
            !toDate ||
            fromDate > toDate
        ) {
            return;
        }

        fetchChartData();
    }, [
        selectedPromptId,
        selectedEnginesKey,
        selectedMetricKey,
        fromDate,
        toDate,
        fetchChartData,
    ]);

    if (loadingDefault) {
        return (
            <LoaderSimChart />
        )
    }

    return (
        <div className="geo_overview_res_cont_chart">
            <div className="geo_overview_res_cont_chart_header">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                        as="button"
                        className="geo_overview_res_cont_chart_btn"
                    >
                        {selectedEnginesChart.length > 0
                            ? `${selectedEnginesChart.length} ${selectedEnginesChart.length > 1 ? t.geo.engines : t.geo.engine}`
                            : t.geo.select_engines}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="custom_dropdown_menu">
                        <div className="dropdown_container fade-in">
                            <div className="scrollable_dropdown_cont">
                                {engines.length > 0 ? (
                                    engines.map((engine) => {
                                        const isSelected =
                                            selectedEnginesChart.includes(engine);
                                        return (
                                            <button
                                                key={engine}
                                                type="button"
                                                className="settings_link"
                                                style={{ textTransform: "capitalize" }}
                                                onClick={() => {
                                                    setSelectedEnginesChart((prev) =>
                                                        prev.includes(engine)
                                                            ? prev.filter((item) => item !== engine)
                                                            : [...prev, engine]
                                                    );
                                                }}
                                            >
                                                <i>
                                                    {isSelected
                                                        ? <MdOutlineCheckCircle />
                                                        : <MdOutlineCircle />}
                                                </i>
                                                <p className="settings_link_prompt">
                                                    {engine}
                                                </p>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="no_data_dropdown fade-in">
                                        <p>
                                            {t?.no_data_found_by_search || "No results found"}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="geo_overview_res_cont_chart_btn geo_overview_res_cont_chart_btn_long"
                    >
                        {selectedPrompt
                            ? selectedPrompt
                                .charAt(0)
                                .toUpperCase() +
                            selectedPrompt.slice(1)
                            : t.geo.select_prompt}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="custom_dropdown_menu">
                        <div className="dropdown_container fade-in">
                            <div className="scrollable_dropdown_cont">
                                {filteredPrompts.length > 0 ? (
                                    filteredPrompts.map((prompt) => (
                                        <button
                                            key={prompt.id}
                                            type="button"
                                            className="settings_link"
                                            style={{ textTransform: "capitalize" }}
                                            onClick={() => {
                                                setSelectedPromptId(prompt.id);
                                                setSelectedPrompt(prompt.prompt);
                                            }}
                                        >
                                            <i>
                                                {selectedPromptId === prompt.id
                                                    ? <MdOutlineCheckCircle />
                                                    : <MdOutlineCircle />
                                                }
                                            </i>

                                            <p className="settings_link_prompt">
                                                {prompt.prompt}
                                            </p>
                                        </button>
                                    ))
                                ) : (
                                    <div className="no_data_dropdown fade-in">
                                        <p>
                                            {t?.no_data_found_by_search || "No results found"}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="filter_dropdown fade-in">
                                <input
                                    className="filter_input"
                                    placeholder={t?.search_filter}
                                    value={filterPrompts}
                                    onChange={(event) => setFilterPrompts(event.target.value)}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="geo_overview_res_cont_chart_btn"
                    >
                        {selectedMetric
                            ? metrics.find((metric) => metric.key === selectedMetricKey)?.label
                            : t.geo.select_metric}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="custom_dropdown_menu">
                        <div className="dropdown_container fade-in">
                            <div className="scrollable_dropdown_cont">
                                {metrics.map((metric) => (
                                    <button
                                        key={metric.key}
                                        type="button"
                                        className="settings_link"
                                        style={{
                                            textTransform: "capitalize",
                                        }}
                                        onClick={() => {
                                            setSelectedMetricKey(metric.key);
                                            setSelectedMetric(metric.label);
                                        }}
                                    >
                                        <i>
                                            {selectedMetricKey === metric.key
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />
                                            }
                                        </i>
                                        <p className="settings_link_prompt">
                                            {metric.label}
                                        </p>
                                    </button>
                                ))
                                }
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                <div className="geo_overview_date_field">
                    <label htmlFor="geo-from-date">{t.geo.from}</label>

                    <CustomDatePicker
                        id="geo-from-date"
                        value={fromDate}
                        maxDate={toDate}
                        onChange={(date) => {
                            setFromDate(date);
                            fetchChartData();
                        }}
                        className="geo_overview_date_input"
                    />
                </div>

                <div className="geo_overview_date_field">
                    <label htmlFor="geo-to-date">{t.geo.to}</label>

                    <CustomDatePicker
                        id="geo-to-date"
                        value={toDate}
                        minDate={fromDate}
                        onChange={(date) => {
                            setToDate(date);
                            fetchChartData();
                        }}
                        className="geo_overview_date_input"
                    />
                </div>

                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="geo_overview_res_cont_chart_btn"
                    >
                        {lineStyle
                            ? t.geo[lineStyle]
                            : t.geo.line_style}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="custom_dropdown_menu">
                        <div className="dropdown_container fade-in">
                            <div className="scrollable_dropdown_cont">
                                {lineStyles.map((line) => (
                                    <button
                                        key={line}
                                        type="button"
                                        className="settings_link"
                                        style={{
                                            textTransform: "capitalize",
                                        }}
                                        onClick={() => {
                                            setLineStyle(line)
                                        }}
                                    >
                                        <i>
                                            {lineStyle === line
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />
                                            }
                                        </i>
                                        <p className="settings_link_prompt">
                                            {t.geo[line]}
                                        </p>
                                    </button>
                                ))
                                }
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="geo_overview_res_cont_chart_container">
                {loadingChartData ? (
                    <LoaderSimChartData daysLenght={daysLenght} />
                ) : (
                    <LineChartStatus fromDate={fromDate} toDate={toDate} data={data} lineStyle={lineStyle} />
                )}
            </div>
        </div>

    )
}

export default GeoOverviewChart